export interface IBlockedDate {
  /**
   * Date range as a tuple:
   * - Single date: [Date, null] - blocks specific date (or recurring month+day if recurring=true)
   * - Date range: [Date, Date] - blocks all dates between start and end (inclusive, or recurring month+day if recurring=true)
   */
  date: [Date, Date | null];
  /** When true, blocks this day+month every year regardless of year. Applies to both single dates and date ranges. */
  recurring?: boolean;
}

export interface IProfilacticOptions {
  tutorials?: IProfilacticTutorialsOptions;

  blockedDates?: IBlockedDate[];
}

export type IProfilacticTutorialsOptions = Partial<
  Record<ProfilacticInspection, string>
>;

export type ProfilacticInspection =
  | 'batteryTest'
  | 'accessories'
  | 'corrosion'
  | 'externalCableSupport'
  | 'internalCleaning'
  | 'oltFanCleaning'
  | 'filterReplacement'
  | 'switchboardVacuuming'
  | 'contactTightening'
  | 'surgeProtector'
  | 'leakProtection'
  | 'ventilationCooling'
  | 'rackExternal'
  | 'batteryExternal';

export const profilacticInspectionsLabels = new Map<
  ProfilacticInspection,
  string
>([
  ['batteryTest', 'Test napájania'],
  ['accessories', 'Kontakty, spoje, zámky vonkajšie uchytenie rozvádzačov'],
  ['corrosion', 'Korózia a jej odstránenie nástrekom'],
  ['externalCableSupport', 'Vonkajšie uchytenie káblov'],
  ['internalCleaning', 'Čistenie ventilátory (klimatizácia) rack'],
  ['oltFanCleaning', 'Čistenie ventilátor OLT'],
  ['filterReplacement', 'Výmena filtrov'],
  ['switchboardVacuuming', 'Vysávanie rozvádzača'],
  ['contactTightening', 'Dotiahnutie kontaktov a spojov, uchytenie prístrojov'],
  ['surgeProtector', 'Kontrola obmedzovačov prepätia'],
  ['leakProtection', 'Ochrana proti zatekaniu / krytie rozvádzača'],
  ['ventilationCooling', 'Vetranie / chladenie'],
  ['rackExternal', 'Vonkajšia kontrola racku'],
  ['batteryExternal', 'Kontrola batérie'],
]);

/**
 * Filters blocked dates for a specific year.
 * Returns Date objects for all blocked dates that apply to the given year.
 * Handles both:
 * - Single dates: [Date, null] (recurring or year-specific)
 * - Date ranges: [Date1, Date2] (all dates between start and end, or recurring month+day ranges)
 */
export function getDisabledDatesForYear(
  blockedDates: IBlockedDate[] | undefined,
  year: number,
): Date[] {
  if (!blockedDates?.length) return [];
  return blockedDates.flatMap((b) => {
    const [startDate, endDate] = b.date;
    const start =
      startDate instanceof Date ? startDate : new Date(startDate as string);
    if (isNaN(start.getTime())) return [];

    // Handle date range [startDate, endDate]
    if (endDate !== null && endDate !== undefined) {
      const end =
        endDate instanceof Date ? endDate : new Date(endDate as string);
      if (isNaN(end.getTime())) return [];

      const rangeStart = new Date(Math.min(start.getTime(), end.getTime()));
      const rangeEnd = new Date(Math.max(start.getTime(), end.getTime()));

      // For recurring ranges, recreate the date range in the given year using month and day
      if (b.recurring) {
        const recurringStart = new Date(
          year,
          rangeStart.getMonth(),
          rangeStart.getDate(),
        );
        const recurringEnd = new Date(
          year,
          rangeEnd.getMonth(),
          rangeEnd.getDate(),
        );
        const disabledDates: Date[] = [];
        const current = new Date(recurringStart);
        while (current <= recurringEnd) {
          disabledDates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
        return disabledDates;
      }

      // For non-recurring ranges, check year overlap
      const yearStart = new Date(year, 0, 1);
      const yearEnd = new Date(year, 11, 31, 23, 59, 59);

      // Check if range overlaps with the requested year
      if (
        rangeStart.getTime() > yearEnd.getTime() ||
        rangeEnd.getTime() < yearStart.getTime()
      ) {
        return []; // No overlap with this year
      }

      // Calculate the overlap between range and year
      const overlapStart = new Date(
        Math.max(rangeStart.getTime(), yearStart.getTime()),
      );
      const overlapEnd = new Date(
        Math.min(rangeEnd.getTime(), yearEnd.getTime()),
      );

      const disabledDates: Date[] = [];
      const current = new Date(overlapStart);
      while (current <= overlapEnd) {
        disabledDates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      return disabledDates;
    }

    // Handle single date [date, null]
    if (b.recurring) {
      return [new Date(year, start.getMonth(), start.getDate())];
    }
    if (start.getFullYear() === year) {
      return [new Date(year, start.getMonth(), start.getDate())];
    }
    return [];
  });
}
